import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import FormBlock from "@common/controls/Form/Block/component";
import Icon from "@common/controls/Icon/component";
import { IconType, SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { Users } from "@common/modules/user/types";
import { isFunction } from "lodash";
import * as React from "react";
import { type Affiliate } from "../../types";
import { KpiBadgeItem, KpiIconItem } from "./KpiItem";

type Props = {
  cycleEndDate: string;
  data: Users.Api.DashboardAffiliate | null;
  isLoading: boolean;
  kpis?: Affiliate.KpiOptionItem[];
  onClick?: (id: string) => void;
  onEditClick?: () => void;
  style?: React.CSSProperties;
};

export const ProfileWidget = (props: Props) => {
  const i18n = useI18n();

  const DEFAULT_KPIS = React.useMemo(
    (): Affiliate.KpiOptionItem[] => [
      { value: "PinkRank", label: i18n.pinkRank },
      { value: "PayRank", label: i18n.payRank },
      { value: "Status", label: i18n.status }
    ],
    [i18n]
  );

  const kpis = React.useMemo((): any[] => {
    if (!props.cycleEndDate) return [];

    return (props.kpis ?? DEFAULT_KPIS).map((x) => {
      if (isFunction(x)) {
        return x({ cycleEndDate: props.cycleEndDate, currencyCode: props.data?.currencyCode });
      }

      if (isFunction(x.value)) {
        return x.value({ cycleEndDate: props.cycleEndDate, currencyCode: props.data?.currencyCode });
      }

      switch (x.value) {
        case "PayRank": {
          return (
            <KpiIconItem
              key="PayRank"
              label={x.label}
              help={i18n.affiliateProfileRankTooltip}
              color={props.data?.rankColor}
              icon={IconType.Crown}
              value={props.data?.rankName}
            />
          );
        }
        case "PinkRank": {
          return (
            <KpiIconItem
              key="PinkRank"
              label={x.label}
              help={i18n.affiliateProfilePinRankTooltip}
              color={props.data?.pinRankColor}
              icon={IconType.Crown}
              value={props.data?.pinRankName}
            />
          );
        }
        case "Status": {
          return (
            <KpiBadgeItem
              key="Status"
              label={x.label}
              color={props.data?.statusName === "Inactive" ? SystemColor.Red : SystemColor.Green}
              value={props.data?.statusName}
            />
          );
        }
      }
    });
  }, [i18n, props.cycleEndDate, props.data, props.kpis]);

  return (
    <FormBlock
      className="affiliate_profile_block"
      isLoading={props.isLoading}
      isNarrow
      title={
        <div className="title_data" onClick={props.onEditClick}>
          {!!props.data && <UserProfilePicture {...pickUserProfilePictureProps(props.data)} size={38} />}
          {!!props.onEditClick && <Icon icon={IconType.Pencil} />}
        </div>
      }
      style={props.style}
    >
      <ul className="items">{kpis}</ul>
    </FormBlock>
  );
};
